import API from './axios';

// Laboratory module services live in their own file (docs/lab-module 06 §1.4).
export * from './labServices';

// Doctor's currently selected clinic (chosen on the post-login selector).
// Sent with outbound messages so the clinic's own WhatsApp number is used.
const selectedClinicId = () => {
  try {
    return JSON.parse(localStorage.getItem('selectedClinic'))?.id || undefined;
  } catch {
    return undefined;
  }
};

// ============ AUTH ============
export const authService = {
  register: (data) => API.post('auth/register/', data),
  login: (email, password) => API.post('auth/login/', { email, password }),
  refreshToken: () => {
    const refresh = localStorage.getItem('refreshToken');
    return API.post('auth/token/refresh/', { refresh });
  },
  getProfile: () => API.get('auth/profile/'),
  updateProfile: (data) => API.put('auth/profile/', data),
  changePassword: (data) => API.post('auth/change-password/', data),
};

// ============ PUBLIC ============
export const publicService = {
  getDoctors: (params) => API.get('doctors/', { params }),
  getDoctorById: (id) => API.get(`doctors/${id}/`),
  getDoctorSlots: (doctorId, date) =>
    API.get(`doctors/${doctorId}/slots/`, { params: { date } }),
  getHospitals: (params) => API.get('hospitals/', { params }),
};

// ============ APPOINTMENTS ============
export const appointmentService = {
  book: (data) => API.post('appointments/', data),
  // Email / WhatsApp the invoice from the payment dialog. Each channel
  // reports its own outcome in the response.
  sendInvoice: (appointmentId, channels) =>
    API.post(`appointments/${appointmentId}/send-invoice/`, { channels }),
  getCustomerAppointments: (params) =>
    API.get('customer/appointments/', { params }),
  getById: (id) => API.get(`appointments/${id}/`),
  cancel: (id) => API.put(`appointments/${id}/cancel/`),
  reschedule: (id, data) => API.put(`appointments/${id}/reschedule/`, data),
  getPrescriptions: () => API.get('customer/prescriptions/'),
  getPrescription: (appointmentId) =>
    API.get(`customer/prescriptions/${appointmentId}/`),
  getPrescriptionPDF: (appointmentId) =>
    API.get(`customer/prescriptions/${appointmentId}/pdf/`, { responseType: 'blob' }),
  getQueueStatus: (appointmentId) =>
    API.get(`appointments/${appointmentId}/queue-status/`),
  generateVideoCall: (appointmentId) =>
    API.post(`appointments/${appointmentId}/video-call/`),
};

// ============ PAYMENTS ============
export const paymentService = {
  createOrder: (data) => API.post('payments/create-order/', data),
  verify: (data) => API.post('payments/verify/', data),
  getPaymentHistory: (params) => API.get('customer/payments/', { params }),
  getInvoice: (id) => API.get(`payments/${id}/invoice/`, { responseType: 'blob' }),
};

// ============ NOTIFICATIONS ============
export const notificationService = {
  getNotifications: (params) => API.get('notifications/', { params }),
  markAsRead: (id) => API.put(`notifications/${id}/read/`),
};

// ============ HOSPITAL ADMIN ============
export const hospitalAdminService = {
  getDashboard: () => API.get('hospital/dashboard/'),

  // Doctors
  getDoctors: (params) => API.get('hospital/doctors/', { params }),
  getDoctorById: (id) => API.get(`hospital/doctors/${id}/`),
  addDoctor: (data) => API.post('hospital/doctors/', data),
  updateDoctor: (id, data) => API.put(`hospital/doctors/${id}/`, data),
  deleteDoctor: (id) => API.delete(`hospital/doctors/${id}/`),

  // Branches
  getBranches: (params) => API.get('hospital/branches/', { params }),
  getBranchById: (id) => API.get(`hospital/branches/${id}/`),
  addBranch: (data) => API.post('hospital/branches/', data),
  updateBranch: (id, data) => API.put(`hospital/branches/${id}/`, data),
  deleteBranch: (id) => API.delete(`hospital/branches/${id}/`),

  // Availability
  getDoctorAvailability: (doctorId) =>
    API.get('hospital/doctor-availability/', { params: { doctor_id: doctorId } }),
  setDoctorAvailability: (doctorId, data) =>
    API.post('hospital/doctor-availability/', { ...data, doctor: doctorId }),
  // Doctor leave / unavailability for any of the clinic's doctors.
  getDoctorLeaves: (doctorId, params) =>
    API.get('doctor-leaves/', { params: { doctor_id: doctorId, ...params } }),
  addDoctorLeave: (data) => API.post('doctor-leaves/', data),
  deleteDoctorLeave: (id) => API.delete(`doctor-leaves/${id}/`),
  getDoctorSchedule: (doctorId, params) =>
    API.get('doctor-schedule/', { params: { doctor_id: doctorId, ...params } }),

  // Appointments
  getAppointments: (params) => API.get('hospital/appointments/', { params }),
  updateAppointmentStatus: (id, status) =>
    API.put(`hospital/appointments/${id}/update/`, { status }),
  // Generic partial update — used to attach/detach an insurance policy.
  updateAppointment: (id, data) =>
    API.put(`hospital/appointments/${id}/update/`, data),
  // Front-desk manual collection (same flow as the doctor's Mark Paid).
  updatePaymentStatus: (id, data) =>
    API.patch(`hospital/appointments/${id}/payment/`, data),

  // Revenue
  getRevenue: (params) => API.get('hospital/revenue/', { params }),

  // Notify patients of doctor delay (hospital staff)
  notifyDelay: (data) => API.post('hospital/notify-delay/', data),

  // Walk-in / phone-in booking on patient's behalf
  bookWalkin: (data) => API.post('hospital/appointments/walkin/', data),

  // Lab partners (catalog.LabPartner), linked from a lab reference lab
  getLabPartners: () => API.get('hospital/lab-partners/'),

  // Corporate Partners / Clients (B2B tie-ups)
  getPartners: (params) => API.get('hospital/partners/', { params }),
  getPartner: (id) => API.get(`hospital/partners/${id}/`),
  createPartner: (data) => API.post('hospital/partners/', data),
  updatePartner: (id, data) => API.patch(`hospital/partners/${id}/`, data),
  deactivatePartner: (id) => API.delete(`hospital/partners/${id}/`),
  getPartnerInvoices: (id) => API.get(`hospital/partners/${id}/invoices/`),

  // Clinic-wide follow-ups (all doctors)
  getFollowUps: (params) => API.get('hospital/follow-ups/', { params }),
  sendFollowUpReminder: (prescriptionId) =>
    API.post(`hospital/follow-ups/${prescriptionId}/remind/`),

  // Coupons & offers
  getCoupons: (params) => API.get('hospital/coupons/', { params }),
  createCoupon: (data) => API.post('hospital/coupons/', data),
  updateCoupon: (id, data) => API.patch(`hospital/coupons/${id}/`, data),
  deleteCoupon: (id) => API.delete(`hospital/coupons/${id}/`),

  // Counseling queue (surgery cases)
  getCounseling: (params) => API.get('hospital/counseling/', { params }),
  createCounseling: (data) => API.post('hospital/counseling/', data),
  getCounselingCase: (id) => API.get(`hospital/counseling/${id}/`),
  updateCounseling: (id, data) => API.patch(`hospital/counseling/${id}/`, data),

  // Feedback (clinic view + aggregates)
  getFeedback: () => API.get('hospital/feedback/'),

  // Communications log (all messages sent to customers)
  getCommunications: () => API.get('hospital/communications/'),

  // WhatsApp inbox — every doctor's patient threads at this clinic
  getChatInbox: () => API.get('hospital/chats/'),
  getChatMessages: (appointmentId) =>
    API.get(`hospital/appointments/${appointmentId}/chat/`),
  sendChatMessage: (appointmentId, message) =>
    API.post(`hospital/appointments/${appointmentId}/chat/send/`, { message }),

  // Customers (patients who've booked with this hospital)
  getCustomers: (params) => API.get('hospital/customers/', { params }),

  // Live doctor availability
  getDoctorLiveAvailability: () => API.get('hospital/doctor-live-availability/'),

  // Customer invoices (consultation / diagnostic / pharmacy)
  getInvoices: (params) => API.get('hospital/invoices/', { params }),
  createInvoice: (data) => API.post('hospital/invoices/', data),
  getInvoice: (id) => API.get(`hospital/invoices/${id}/`),
  updateInvoice: (id, data) => API.patch(`hospital/invoices/${id}/`, data),
  getInvoicePDF: (id) => API.get(`invoices/${id}/pdf/`, { responseType: 'blob' }),
};

// ============ COUPONS ============
export const couponService = {
  // Preview a coupon's discount (usable by clinic staff + doctors).
  validate: (data) => API.post('coupons/validate/', data),
};

// ============ FEEDBACK ============
export const feedbackService = {
  // Customer portal
  getMyFeedback: () => API.get('customer/feedback/'),
  submitFeedback: (data) => API.post('customer/feedback/', data),
  // Public shareable link / QR
  getPublicContext: (appointmentId) => API.get(`public/feedback/${appointmentId}/`),
  submitPublic: (appointmentId, data) =>
    API.post(`public/feedback/${appointmentId}/`, data),
};

// ============ CUSTOMER INVOICES ============
export const invoiceService = {
  getMyInvoices: (params) => API.get('customer/invoices/', { params }),
  getPDF: (id) => API.get(`invoices/${id}/pdf/`, { responseType: 'blob' }),
};

// ============ DOCTOR ============
export const doctorService = {
  getDashboard: () => API.get('doctor/dashboard/'),
  // Planner — every appointment across all hospitals the doctor visits.
  // Backs both the List and Calendar views on the landing screen.
  getPlanner: (params) => API.get('doctor/planner/', { params }),
  getAppointments: (params) => API.get('doctor/appointments/', { params }),
  getAppointmentContext: (id) => API.get(`doctor/appointments/${id}/context/`),
  updateAppointmentStatus: (id, data) =>
    API.put(`doctor/appointments/${id}/update/`, data),
  // Generic partial update — used to attach/detach an insurance policy.
  updateAppointment: (id, data) =>
    API.put(`doctor/appointments/${id}/update/`, data),
  // Inline remarks editor (reuses the appointment update endpoint).
  updateAppointmentRemarks: (id, remarks) =>
    API.put(`doctor/appointments/${id}/update/`, { remarks }),
  // Payment status — manual collection (with method) or refresh online state.
  updatePaymentStatus: (id, data) =>
    API.patch(`doctor/appointments/${id}/payment/`, data),
  // Leave / unavailability (own). POST with dry_run: true only previews.
  getLeaves: (params) => API.get('doctor-leaves/', { params }),
  addLeave: (data) => API.post('doctor-leaves/', data),
  deleteLeave: (id) => API.delete(`doctor-leaves/${id}/`),
  // Per-date summary (windows, slots, bookings, leave) for a date range.
  getSchedule: (params) => API.get('doctor-schedule/', { params }),
  getAvailability: () => API.get('doctor/availability/'),
  addAvailability: (data) => API.post('doctor/availability/', data),
  updateAvailability: (id, data) => API.put(`doctor/availability/${id}/`, data),
  deleteAvailability: (id) => API.delete(`doctor/availability/${id}/`),
  createPrescription: (data) => API.post('doctor/prescriptions/', data),
  getPrescription: (appointmentId) =>
    API.get(`doctor/prescriptions/${appointmentId}/`),
  updatePrescription: (appointmentId, data) =>
    API.put(`doctor/prescriptions/${appointmentId}/`, data),
  getPatientHistory: (customerId) =>
    API.get(`doctor/patients/${customerId}/history/`),
  getChatInbox: () => API.get('doctor/chats/'),
  // New inquiries — WhatsApp senders who are not patients yet
  getInquiries: () => API.get('doctor/inquiries/'),
  getInquiryMessages: (contactId) =>
    API.get(`doctor/inquiries/${contactId}/messages/`),
  sendInquiryMessage: (contactId, message) =>
    API.post(`doctor/inquiries/${contactId}/send/`, { message }),
  updateInquiryStatus: (contactId, status) =>
    API.patch(`doctor/inquiries/${contactId}/`, { status }),
  convertInquiry: (contactId, appointmentId) =>
    API.post(`doctor/inquiries/${contactId}/convert/`, {
      appointment_id: appointmentId,
    }),
  getChatMessages: (appointmentId) =>
    API.get(`doctor/appointments/${appointmentId}/chat/`),
  // Prescription-ready notice — template-first (delivers outside 24h window)
  notifyPrescription: (appointmentId, templateName, templateParams, mediaUrl) =>
    API.post(`doctor/appointments/${appointmentId}/prescription-notify/`, {
      template_name: templateName || undefined,
      template_params: templateParams || undefined,
      media_url: mediaUrl || undefined,
    }),
  // WhatsApp template registry
  getWhatsAppTemplates: () => API.get('doctor/whatsapp-templates/'),
  addWhatsAppTemplate: (data) => API.post('doctor/whatsapp-templates/', data),
  syncWhatsAppTemplates: () => API.post('doctor/whatsapp-templates/sync/'),
  uploadWhatsAppTemplateMedia: (formData) =>
    API.post('doctor/whatsapp-templates/upload-media/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  sendChatMessage: (appointmentId, message) =>
    API.post(`doctor/appointments/${appointmentId}/chat/send/`, {
      message,
      hospital_id: selectedClinicId(),
    }),
  uploadRecording: (appointmentId, formData) =>
    API.post(`doctor/appointments/${appointmentId}/recordings/`, formData),
  getRecordings: (appointmentId) =>
    API.get(`doctor/appointments/${appointmentId}/recordings/`),
  notifyDelay: (data) => API.post('doctor/notify-delay/', data),
  // One-off patient message (WhatsApp). Accepts { appointment_id?, phone?, message }.
  sendMessage: (data) =>
    API.post('doctor/messages/send/', { hospital_id: selectedClinicId(), ...data }),
  getQueue: (params) => API.get('doctor/queue/', { params }),
  addToQueue: (data) => API.post('doctor/queue/', data),
  updateQueueEntry: (id, data) => API.put(`doctor/queue/${id}/update/`, data),
  // Check-in & Availability
  checkIn: () => API.post('doctor/check-in/'),
  checkOut: () => API.delete('doctor/check-in/'),
  getAvailabilityStatus: () => API.get('doctor/availability-status/'),
  toggleAvailability: (isAvailable) =>
    API.put('doctor/availability-status/', { is_available: isAvailable }),
  // Slot Management
  getSlots: (params) => API.get('doctor/slots/', { params }),
  blockSlot: (slotId) => API.put(`doctor/slots/${slotId}/block/`),
  unblockSlot: (slotId) => API.put(`doctor/slots/${slotId}/unblock/`),
  // Walk-in / on-behalf booking (same form as clinic) + corporate partners
  bookWalkin: (data) => API.post('doctor/appointments/walkin/', data),
  getPartners: (params) => API.get('doctor/partners/', { params }),
  // Follow-ups
  getFollowUps: (params) => API.get('doctor/follow-ups/', { params }),
  sendFollowUpReminder: (prescriptionId) =>
    API.post(`doctor/follow-ups/${prescriptionId}/remind/`),
  getReminderSettings: () => API.get('doctor/reminder-settings/'),
  updateReminderSettings: (data) => API.put('doctor/reminder-settings/', data),
  // Payments
  getPayments: (params) => API.get('doctor/payments/', { params }),
  getPatientPaymentBifurcation: (params) =>
    API.get('doctor/patients/payment-status/', { params }),
  getPharmacyPrescriptions: (params) =>
    API.get('doctor/pharmacy-prescriptions/', { params }),
  getLabPrescriptions: (params) =>
    API.get('doctor/lab-prescriptions/', { params }),
};

// ============ AUDIT TRAIL ============
export const auditService = {
  // Per-record change history. model: 'appointment' | 'prescription' | ...
  getTrail: (model, objectId, params = {}) =>
    API.get('audit/', { params: { model, object_id: objectId, ...params } }),
};

// ============ QUEUE (Patient-facing) ============
export const queueService = {
  getQueueStatus: (appointmentId) =>
    API.get(`appointments/${appointmentId}/queue-status/`),
};

// ============ KIOSK (public waiting-room display) ============
export const kioskService = {
  getDoctors: () => API.get('kiosk/doctors/'),
  getQueue: (doctorId) => API.get(`kiosk/queue/${doctorId}/`),
};

// ============ CATALOG ============
export const catalogService = {
  // Medicines
  // `autocomplete` selects the lightweight serializer (id/name/strength/
  // label). Without it the endpoint returns full medicine rows, which is
  // what the admin catalogue table needs.
  searchMedicines: (search) =>
    API.get('catalog/medicines/', { params: { search, autocomplete: 1 } }),
  getMedicines: (params) => API.get('catalog/medicines/', { params }),
  createMedicine: (data) => API.post('catalog/medicines/', data),
  updateMedicine: (id, data) => API.put(`catalog/medicines/${id}/`, data),
  deleteMedicine: (id) => API.delete(`catalog/medicines/${id}/`),
  // Medical Tests
  // `autocomplete` selects the lightweight serializer; without it the
  // endpoint returns full test rows, which the admin table needs.
  searchTests: (search) =>
    API.get('catalog/tests/', { params: { search, autocomplete: 1 } }),
  getTests: (params) => API.get('catalog/tests/', { params }),
  createTest: (data) => API.post('catalog/tests/', data),
  updateTest: (id, data) => API.put(`catalog/tests/${id}/`, data),
  deleteTest: (id) => API.delete(`catalog/tests/${id}/`),
  // Test Panels (reusable diagnostic bundles)
  getTestPanels: (params) => API.get('catalog/test-panels/', { params }),
  createTestPanel: (data) => API.post('catalog/test-panels/', data),
  updateTestPanel: (id, data) => API.patch(`catalog/test-panels/${id}/`, data),
  deleteTestPanel: (id) => API.delete(`catalog/test-panels/${id}/`),
  // Medicine Packs (reusable prescription bundles)
  getMedicinePacks: (params) => API.get('catalog/medicine-packs/', { params }),
  createMedicinePack: (data) => API.post('catalog/medicine-packs/', data),
  updateMedicinePack: (id, data) => API.patch(`catalog/medicine-packs/${id}/`, data),
  deleteMedicinePack: (id) => API.delete(`catalog/medicine-packs/${id}/`),
};

// ============ REFERRALS ============
export const referralService = {
  getDoctorReferrals: (params) => API.get('doctor/referrals/', { params }),
  createReferral: (data) => API.post('doctor/referrals/', data),
};

// ============ KNOWLEDGE BASE ============
export const knowledgeService = {
  getCategories: () => API.get('knowledge/categories/'),
  createCategory: (data) => API.post('knowledge/categories/', data),
  getArticles: (params) => API.get('knowledge/articles/', { params }),
  getArticle: (id) => API.get(`knowledge/articles/${id}/`),
  createArticle: (data) => API.post('knowledge/articles/', data),
  updateArticle: (id, data) => API.put(`knowledge/articles/${id}/`, data),
  deleteArticle: (id) => API.delete(`knowledge/articles/${id}/`),
  // Attachments (multipart)
  listAttachments: (articleId) =>
    API.get(`knowledge/articles/${articleId}/attachments/`),
  uploadAttachment: (articleId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post(`knowledge/articles/${articleId}/attachments/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAttachment: (attachmentId) =>
    API.delete(`knowledge/attachments/${attachmentId}/`),
};

// ============ PLATFORM ADMIN ============
export const adminService = {
  getDashboard: () => API.get('admin/dashboard/'),

  // Hospitals
  getHospitals: (params) => API.get('admin/hospitals/', { params }),
  getHospitalById: (id) => API.get(`admin/hospitals/${id}/`),
  createHospital: (data) => API.post('admin/hospitals/', data),
  updateHospital: (id, data) => API.put(`admin/hospitals/${id}/`, data),
  createHospitalAdmin: (data) => API.post('admin/hospital-admins/', data),
  // Branches (under a hospital)
  getHospitalBranches: (hospitalId) => API.get(`admin/hospitals/${hospitalId}/branches/`),
  getHospitalBranch: (hospitalId, branchId) => API.get(`admin/hospitals/${hospitalId}/branches/${branchId}/`),
  createHospitalBranch: (hospitalId, data) => API.post(`admin/hospitals/${hospitalId}/branches/`, data),
  updateHospitalBranch: (hospitalId, branchId, data) => API.patch(`admin/hospitals/${hospitalId}/branches/${branchId}/`, data),
  deleteHospitalBranch: (hospitalId, branchId) => API.delete(`admin/hospitals/${hospitalId}/branches/${branchId}/`),

  // Users
  getUsers: (params) => API.get('admin/users/', { params }),
  updateUserStatus: (id, status) =>
    API.put(`admin/users/${id}/`, { status }),

  // Doctors
  getDoctors: (params) => API.get('admin/doctors/', { params }),
  getDoctorById: (id) => API.get(`admin/doctors/${id}/`),
  createDoctor: (data) => API.post('admin/doctors/', data),
  updateDoctor: (id, data) => API.patch(`admin/doctors/${id}/`, data),
  deleteDoctor: (id) => API.delete(`admin/doctors/${id}/`),

  // Appointments
  getAppointments: (params) => API.get('admin/appointments/', { params }),

  // Revenue
  getRevenue: (params) => API.get('admin/revenue/', { params }),
};

// ============ WHATSAPP INTEGRATION ============
export const integrationService = {
  // Global default config (platform admin)
  getWhatsAppConfig: () => API.get('integrations/whatsapp/'),
  saveWhatsAppConfig: (data) => API.put('integrations/whatsapp/', data),
  testWhatsApp: (phone) => API.post('integrations/whatsapp/test/', { phone }),
  // Per-clinic config (platform admin or that clinic's hospital admin)
  getHospitalWhatsAppConfig: (hospitalId) =>
    API.get(`hospitals/${hospitalId}/integrations/whatsapp/`),
  saveHospitalWhatsAppConfig: (hospitalId, data) =>
    API.put(`hospitals/${hospitalId}/integrations/whatsapp/`, data),
  testHospitalWhatsApp: (hospitalId, phone) =>
    API.post(`hospitals/${hospitalId}/integrations/whatsapp/test/`, { phone }),
  // Per-doctor config (doctor's personal WhatsApp number)
  getDoctorWhatsAppConfig: (doctorId) =>
    API.get(`doctors/${doctorId}/integrations/whatsapp/`),
  saveDoctorWhatsAppConfig: (doctorId, data) =>
    API.put(`doctors/${doctorId}/integrations/whatsapp/`, data),
  testDoctorWhatsApp: (doctorId, phone) =>
    API.post(`doctors/${doctorId}/integrations/whatsapp/test/`, { phone }),
};


// Customer insurance — searchable policy master with coverage lines.
export const insuranceService = {
  search: (params) => API.get('insurance/search/', { params }),
  listPolicies: (params) => API.get('insurance/policies/', { params }),
  createPolicy: (data) => API.post('insurance/policies/', data),
  getPolicy: (id) => API.get(`insurance/policies/${id}/`),
  updatePolicy: (id, data) => API.patch(`insurance/policies/${id}/`, data),
  deletePolicy: (id) => API.delete(`insurance/policies/${id}/`),
};

// Per-hospital payment gateway configuration.
export const paymentGatewayService = {
  // Platform admins must pass { hospital: <uuid> }; hospital admins need nothing.
  getConfig: (params) => API.get('payments/gateway-config/', { params }),
  updateConfig: (data, params) =>
    API.patch('payments/gateway-config/', data, { params }),
  // Methods a hospital currently accepts — drives the checkout method list.
  getMethods: (hospital) =>
    API.get('payments/gateway-methods/', { params: { hospital } }),
};
